import { useEffect, useState } from 'react';
import { Spin, Typography } from 'antd';
import { Navigate, useNavigate, useParams } from 'react-router-dom';
import type { CreatePrestigeConfigurationResponse } from '@core';
import {
  useAnonymousAuth,
  useCreatePrestigeConfiguration,
  usePrestigeConfiguration,
  useSavePrestigeConfiguration,
} from '@hooks';
import { ConfiguratorPage } from './ConfiguratorPage';

const { Text } = Typography;

export const CollaborativeConfiguratorPage = () => {
  const { configId } = useParams<{ configId: string }>();
  const navigate = useNavigate();
  const [isRedirectingMissingConfig, setIsRedirectingMissingConfig] =
    useState(false);
  const { uid, isLoading: isAuthLoading, error: authError } = useAnonymousAuth();

  const createConfigurationMutation = useCreatePrestigeConfiguration();
  const saveConfigurationMutation = useSavePrestigeConfiguration(configId);
  const configurationQuery = usePrestigeConfiguration(configId, {
    enabled: !isAuthLoading && !authError,
  });

  useEffect(() => {
    if (!configId) {
      return;
    }

    if (
      isAuthLoading ||
      authError ||
      configurationQuery.isPending ||
      configurationQuery.data !== null ||
      isRedirectingMissingConfig
    ) {
      return;
    }

    setIsRedirectingMissingConfig(true);

    createConfigurationMutation.mutate(undefined, {
      onSuccess: ({ buildNumber }: CreatePrestigeConfigurationResponse) => {
        navigate(`/${buildNumber}`, { replace: true });
      },
      onError: () => {
        setIsRedirectingMissingConfig(false);
      },
    });
  }, [
    authError,
    configId,
    configurationQuery.data,
    configurationQuery.isPending,
    createConfigurationMutation,
    isAuthLoading,
    isRedirectingMissingConfig,
    navigate,
  ]);

  if (!configId) {
    return <Navigate to="/" replace />;
  }

  if (authError) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">Authentication failed: {authError}</Text>
      </div>
    );
  }

  if (configurationQuery.error) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">
          Failed to load configuration. Please refresh and try again.
        </Text>
      </div>
    );
  }

  if (createConfigurationMutation.error) {
    return (
      <div style={{ padding: 24 }}>
        <Text type="danger">
          Failed to recover missing configuration. Please refresh and try again.
        </Text>
      </div>
    );
  }

  if (
    isAuthLoading ||
    configurationQuery.isPending ||
    isRedirectingMissingConfig ||
    !uid ||
    !configurationQuery.data
  ) {
    return (
      <div
        style={{
          height: '100vh',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 16,
        }}
      >
        <Spin size="large" />
        <Text>
          {isRedirectingMissingConfig
            ? 'Configuration not found. Creating a new one...'
            : '10+ Trillion Options Are Loading…'}
        </Text>
      </div>
    );
  }

  return (
    <ConfiguratorPage
      configId={configId}
      ownerID={uid}
      remoteConfiguration={configurationQuery.data}
      saveConfiguration={saveConfigurationMutation.saveConfiguration}
    />
  );
};
